import { PlatformAccessStatus, PlatformAccessType, PlatformKey, PlatformState } from ':constants/index';

import { getPlatformDefinition } from './platforms';

const ACCESS_TYPES = new Set(Object.values(PlatformAccessType));

export function getPlatformState({
    platformKey,
    isInRestaurantPlatforms,
    restaurantAccess,
}: {
    platformKey: PlatformKey;
    isInRestaurantPlatforms: boolean;
    restaurantAccess: Partial<Record<PlatformKey, PlatformAccessType>>;
}): PlatformState {
    if (platformKey === PlatformKey.MAPSTR) {
        return isInRestaurantPlatforms ? PlatformState.CONNECTED : PlatformState.EMPTY;
    }
    if (!isInRestaurantPlatforms) {
        return PlatformState.EMPTY;
    }
    if (!restaurantAccess[platformKey] || !ACCESS_TYPES.has(restaurantAccess[platformKey])) {
        return PlatformState.FOUND;
    }
    return PlatformState.CONNECTED;
}

/** Minimal shape of a restaurant access entry needed to resolve a platform's connection status. */
export interface PlatformAccessStatusInput {
    platformKey: PlatformKey;
    status: PlatformAccessStatus;
    accessType: PlatformAccessType;
    active?: boolean;
}

/**
 * Resolves the connection status of a single platform, applying the platform-specific business rules:
 * - Mapstr is always verified (automatic connection).
 * - Booking is verified only when the restaurant is Yext-ready and the platform document exists.
 * - Platforms that fetch permissions (OAuth/social) derive their status from the live permission result
 *   (TripAdvisor keeps its access status when permissions are valid, as access can still be under review).
 * - Other platforms use the active access status, falling back to the last access or `UNVERIFIED`.
 *
 * Returns `undefined` for permission-based platforms whose permission status has not been resolved yet
 * (so callers can render a loading state).
 */
export function computePlatformAccessStatus({
    platformKey,
    isYextReady,
    hasPlatformData,
    access,
    permissionsStatus,
}: {
    platformKey: PlatformKey;
    isYextReady: boolean;
    hasPlatformData: boolean;
    access: PlatformAccessStatusInput[];
    permissionsStatus: Partial<Record<PlatformKey, boolean>>;
}): PlatformAccessStatus | undefined {
    if (platformKey === PlatformKey.MAPSTR) {
        return PlatformAccessStatus.VERIFIED;
    }

    if (platformKey === PlatformKey.BOOKING && isYextReady && hasPlatformData) {
        return PlatformAccessStatus.VERIFIED;
    }

    const platformAccesses = (access ?? []).filter((pl) => pl.platformKey === platformKey && pl.accessType !== PlatformAccessType.AUTO);

    const platformAccess = platformAccesses.find((pl) => pl.active);

    if (getPlatformDefinition(platformKey)?.shouldFetchPermissions) {
        if (typeof permissionsStatus[platformKey] === 'undefined') {
            return undefined;
        }
        switch (platformKey) {
            case PlatformKey.TRIPADVISOR: // in case of tripadvisor, access can be under verification (permissions will be valid) so we check the access.status
                return permissionsStatus[platformKey] === false ? PlatformAccessStatus.MISSING_PERMISSIONS : platformAccess?.status;
            default:
                return permissionsStatus[platformKey] ? PlatformAccessStatus.VERIFIED : PlatformAccessStatus.MISSING_PERMISSIONS;
        }
    }

    if (platformAccesses.length && !platformAccess) {
        return platformAccesses[platformAccesses.length - 1]?.status;
    }

    return platformAccess?.status || PlatformAccessStatus.UNVERIFIED;
}
